import React, { useCallback, useState } from 'react';
import { Button, Checkbox, Divider, FormControlLabel, Grid, Typography } from '@material-ui/core';
import EventBusyIcon from '@material-ui/icons/EventBusy';
import EventNoteIcon from '@material-ui/icons/EventNote';
import { useActivity, useActivityValueUpdate } from '../activity.context';
import { SaveButton } from '../saveButton.component';
import { YearlyPeriods } from './periods.component';
import { YearSelect } from './YearSelect.component';
import dayjs from 'dayjs';
import { buildYearlyPricingPeriods } from './buildYearlyPricingPeriods';
import { Exceptions } from './exceptions.component';

/**
 * Formulaire de gestion des périodes de tarification des activités
 * 
 * Ce composant permet de :
 * - Définir les périodes d'ouverture par année
 * - Gérer les exceptions de fermeture
 * - Ajouter de nouvelles saisons
 * - Appliquer les changements à toutes les activités
 * 
 * Logique métier :
 * - Gestion multi-année des périodes de tarification
 * - Périodes d'ouverture avec dates de début/fin
 * - Exceptions de fermeture pour les activités non supervisées
 * - Option d'application globale des changements
 * - Sauvegarde conditionnelle selon l'option sélectionnée
 * 
 * @returns Composant React du formulaire de périodes
 */
export const PeriodsForm: React.FC = () => {
  const { activity, save } = useActivity();
  const updatePricingPeriods = useActivityValueUpdate('pricingPeriods');

  /**
   * États locaux pour la gestion des périodes
   */
  const allPeriodsYears = Object.keys(activity.pricingPeriods || {}).sort();
  const latestYear = allPeriodsYears[allPeriodsYears.length - 1];
  const [selectedYear, setSelectedYear] = useState<string>(
    latestYear || String(dayjs().year()),
  );
  const [savePeriodsForAll, setSavePeriodsForAll] = useState<boolean>(false);

  /**
   * Fonction de mise à jour des périodes d'ouverture
   * 
   * @param newPeriods Nouvelles périodes d'ouverture
   */
  const onPeriodsChange = useCallback(
    newPeriods => {
      const newYearPricingPeriods: CRM.YearlyPricingPeriods = {
        ...activity.pricingPeriods![selectedYear],
        periods: newPeriods,
      };
      updatePricingPeriods({
        ...activity.pricingPeriods,
        [selectedYear]: newYearPricingPeriods,
      });
    },
    [updatePricingPeriods, selectedYear, activity.pricingPeriods],
  );

  /**
   * Fonction de mise à jour des exceptions de fermeture
   * 
   * @param newPeriods Nouvelles exceptions de fermeture
   */
  const onPeriodsExceptionsChange = useCallback(
    newPeriods => {
      const newYearPricingPeriods: CRM.YearlyPricingPeriods = {
        ...activity.pricingPeriods![selectedYear],
        exceptions: newPeriods,
      };
      updatePricingPeriods({
        ...activity.pricingPeriods,
        [selectedYear]: newYearPricingPeriods,
      });
    },
    [updatePricingPeriods, selectedYear, activity.pricingPeriods],
  );

  /**
   * Fonction d'ajout d'une nouvelle année de périodes
   */
  const addPeriodYear = useCallback(() => {
    const newYear = latestYear || dayjs().year();
    const newYearPricingPeriods = buildYearlyPricingPeriods(
      Number(newYear) + 1,
    );
    updatePricingPeriods({
      ...activity.pricingPeriods,
      ...newYearPricingPeriods,
    });
  }, [updatePricingPeriods, latestYear, activity.pricingPeriods]);

  return (
    <form onSubmit={(evt) => save(evt, savePeriodsForAll === true ? selectedYear : undefined)}>
      <Grid container spacing={3}>
        <Grid item xs={12} container spacing={3} alignItems={'center'}>
          <Grid item>
            <YearSelect
              value={selectedYear}
              onChange={setSelectedYear}
              years={allPeriodsYears}
            />
          </Grid>
          <Grid item>
            <Button
              color="secondary"
              variant="contained"
              onClick={addPeriodYear}
            >
              Ajouter une saison
            </Button>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <Divider variant="middle" />
        </Grid>
        <Grid item xs={12}>
          <Typography variant="h5">
            <EventNoteIcon /> Périodes d'ouverture
          </Typography>
          {activity._id && 
            <FormControlLabel
              control={
                <Checkbox
                  color="secondary"
                  onChange={event => {
                    setSavePeriodsForAll(event.target.checked);
                  }}
                  checked={savePeriodsForAll}
                />
              }
              label="Appliquer ce changement à toutes les activités"
              labelPlacement="start"
            />
          }
        </Grid>
        {activity.pricingPeriods && activity.pricingPeriods[selectedYear] && (
          <Grid item xs={12}>
            <YearlyPeriods
              year={Number(selectedYear)}
              pricingPeriods={activity.pricingPeriods[selectedYear]}
              onPeriodsChange={onPeriodsChange}
            />
          </Grid>
        )}
        {activity.kind !== "supervised" && <Grid item xs={12}>
          <Typography variant="h5">
            <EventBusyIcon /> Exceptions
          </Typography>
        </Grid>}
        {activity.kind !== "supervised" && activity.pricingPeriods && activity.pricingPeriods[selectedYear] && (
          <Grid item xs={12} container spacing={3} alignItems={'center'}>
            <Exceptions
              year={Number(selectedYear)}
              value={activity.pricingPeriods[selectedYear].exceptions}
              onChange={onPeriodsExceptionsChange}
            />
          </Grid>
        )}
        <Grid item xs={12}>
          <SaveButton />
        </Grid>
      </Grid>
    </form>
  );
};
